/**
 * EL DESPLEGABLE DE SOFTFI.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO SE USA EL `<select>` DEL NAVEGADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño, con la lista de «Moneda del vuelto» abierta en la pantalla del
 * cobro: «son bien genéricas y feas». Y es verdad: esa lista gris o azul no la
 * dibuja Softfi, la dibuja Windows, y cambia de un navegador a otro. Era lo
 * único de la aplicación que no tenía nuestra cara, y estaba en ochenta y
 * cinco sitios, empezando por la pantalla más importante de todas.
 *
 * Esto es la pieza que dibuja TODOS los desplegables: los de toda la vida
 * (`Lista`, en `ui.tsx`, que por fuera se usa igual que un `<select>`) y el
 * buscable de las categorías (`SelectorBuscable`). Una sola pieza, así se ven y
 * se manejan igual en todas partes, y lo que se mejore aquí mejora los cien.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE EL `<select>` HACÍA BIEN Y NO SE PODÍA PERDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  · **El teclado.** El cajero rápido va con Tab. Flechas, Enter, Escape, y
 *    escribir una letra salta a la opción que empieza por ella.
 *  · **El dedo.** En el celular, el `<select>` abría la lista del teléfono,
 *    grande y cómoda. Aquí las filas crecen en pantallas táctiles.
 *  · **No salirse nunca de la pantalla.** Si no cabe abajo, se abre hacia
 *    arriba, y el alto se ajusta al hueco que hay de verdad.
 *  · **El primer toque fuera solo cierra.** Como el `<select>`: si la lista
 *    está abierta dentro del cobro y se toca fuera, se cierra la lista, no se
 *    pulsa lo que había debajo ni se cierra la ventana.
 */

import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { ArrowUpDown, Check, ChevronDown, Plus, Search, X } from "lucide-react";
import { contieneSinTildes } from "@/lib/sinTildes";
import { VerMas } from "@/components/VerMas";

export type OpcionDesplegable = {
  valor: string;
  etiqueta: string;
  /** Un punto de color a la izquierda. Para categorías. */
  color?: string | null;
  /** Una segunda línea, más pequeña. */
  pie?: string;
  /** Se ve, pero no se puede elegir. */
  desactivada?: boolean;
};

/** A partir de aquí, la lista se busca en vez de recorrerse. */
export const DESDE_CUANTAS_SE_BUSCA = 8;

/**
 * Une clases saltándose las vacías.
 *
 * Es el mismo `cx` de `ui.tsx`, repetido a propósito: `ui.tsx` importa este
 * archivo para dibujar `Lista`, y si este importara `ui.tsx` quedaría un
 * círculo de importaciones que Vite resuelve mal al recargar en caliente.
 */
function unir(...clases: (string | false | null | undefined)[]): string {
  return clases.filter(Boolean).join(" ");
}

function sinTildes(texto: string): string {
  return texto.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 * ¿La clase que viene de fuera ya decide esto? Entonces la de base se quita.
 *
 * ⚠️ No basta con ponerla detrás: con dos clases de la misma propiedad
 * (`h-11` y `h-9`), gana la que Tailwind escriba más abajo en la hoja, no la
 * que se escribió después. Por eso existe también `pidioSuAncho` en `ui.tsx`.
 */
function pide(className: string | undefined, patron: RegExp): boolean {
  return patron.test(className ?? "");
}

export type DesplegableProps = {
  valor: string;
  opciones: OpcionDesplegable[];
  onElegir: (valor: string) => void;

  /**
   * Qué se enseña si `valor` no es ninguna de las opciones.
   *
   *  · "primera" — la primera que se pueda elegir, como hace React con un
   *    `<select>`. Es lo que usa `Lista`, para que ninguna pantalla cambie de
   *    comportamiento: solo de cara.
   *  · "texto" — `textoSinElegir`, en gris. Lo del selector de categorías.
   */
  siNoCoincide?: "primera" | "texto";
  textoSinElegir?: string;

  /** Una fila de «ninguna» arriba del todo, con valor vacío. */
  opcionNinguna?: string;
  /** Si viene, se ofrece crear lo que se escribió y no existe. */
  onCrear?: (nombre: string) => void | Promise<void>;
  /**
   * El verbo del botón de crear: «Crear «Bebidas»». Donde lo escrito no se
   * guarda en ninguna lista —el tipo de empaque de un traslado— es «Usar».
   */
  verboCrear?: string;
  /** La pista de abajo, antes de escribir. Por defecto, la de crear. */
  pistaCrear?: string;

  /**
   * Si viene, lo que se escribe en el buscador se le pasa a quien llama, que
   * trae las opciones del servidor: aquí la lista NO se filtra. Es para lo que
   * no cabe en una sola carga, como el catálogo de un negocio de tres mil
   * productos (ver `BuscadorDeProducto` en Estadísticas).
   *
   * ⚠️ No se vuelve a filtrar aquí porque el servidor encuentra también por
   * código de barras, que no está en la etiqueta: filtrar por nombre lo que ya
   * llegó borraría justo lo que se buscó por código.
   */
  onBuscar?: (texto: string) => void;
  /**
   * Con `onBuscar`: cuántas opciones faltan por traer y cómo pedirlas. Salen
   * solas al bajar hasta el final de la lista (ver `VerMas`).
   */
  quedan?: number;
  onVerMas?: () => void;
  cargandoMas?: boolean;

  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  id?: string;
  title?: string;
  "aria-label"?: string;
  /** Va al botón: tamaño, ancho, márgenes. */
  className?: string;

  /**
   * Cómo se ve el botón cerrado (2026-10-18).
   *
   *  · "campo" — la casilla de siempre, del ancho del formulario.
   *  · "pastilla" — un filtro de `BarraDeFiltros`: «Marca ⌄» sin elegir;
   *    elegida, «Marca: Polar» rellena con el acento y con su ✕ para quitarla
   *    (vuelve a `""`). Necesita `nombre`.
   *  · "orden" — el «Ordenar: Nombre (A–Z)» de al lado del buscador; en el
   *    celular, solo la flecha.
   *
   * Las dos últimas, en un celular, abren la lista como una hoja desde abajo:
   * son filtros de una pantalla llena de cosas y ahí se eligen con el pulgar.
   */
  forma?: "campo" | "pastilla" | "orden";
  /** El nombre del filtro: «Marca». Sale en la pastilla y arriba de la hoja. */
  nombre?: string;
};

type Sitio = {
  top?: number;
  bottom?: number;
  left: number;
  minAncho: number;
  maxAlto: number;
  haciaArriba: boolean;
};

const MARGEN = 8;
const SEPARACION = 6;
const ALTO_MAXIMO = 360;

export const Desplegable = forwardRef<HTMLButtonElement, DesplegableProps>(
  function Desplegable(
    {
      valor,
      opciones,
      onElegir,
      siNoCoincide = "texto",
      textoSinElegir = "Elige…",
      opcionNinguna,
      onCrear,
      verboCrear = "Crear",
      pistaCrear = "¿No está en la lista? Escríbelo arriba y lo creas aquí mismo.",
      onBuscar,
      quedan = 0,
      onVerMas,
      cargandoMas = false,
      disabled = false,
      error = false,
      autoFocus,
      id,
      title,
      "aria-label": ariaLabel,
      className,
      forma = "campo",
      nombre,
    },
    refExterno,
  ) {
    const [abierto, setAbierto] = useState(false);
    /** Si esta vez se abrió como hoja desde abajo. Se decide al abrir. */
    const [hoja, setHoja] = useState(false);
    const [busqueda, setBusqueda] = useState("");
    const [activa, setActiva] = useState(-1);
    const [sitio, setSitio] = useState<Sitio | null>(null);

    const boton = useRef<HTMLButtonElement | null>(null);
    const panel = useRef<HTMLDivElement | null>(null);
    const buscador = useRef<HTMLInputElement | null>(null);
    const letras = useRef({ texto: "", hasta: 0 });
    /*
      La animación de entrada se decide UNA vez, al abrir. Si dependiera de
      dónde está la lista en cada momento, al girar el celular o abrirse el
      teclado la lista cambiaba de lado, cambiaba de clase y volvía a aparecer
      desde transparente: un parpadeo en medio de elegir.
    */
    const entradaDesde = useRef<"abajo" | "arriba" | null>(null);

    const base = useId();
    const idCampo = id ?? `${base}-campo`;
    const idLista = `${base}-lista`;
    const idFila = (i: number) => `${base}-op-${i}`;

    /*
      ⚠️ CON `onCrear` EL BUSCADOR SALE SIEMPRE, HAYA LAS OPCIONES QUE HAYA.

      El umbral de ocho existe para no poner una lupa encima de tres opciones
      que se leen de un vistazo. Pero cuando se puede CREAR, ese cuadro no es un
      buscador: es el único sitio donde se escribe lo nuevo. Se vio con las
      categorías de gasto, que nacen siendo siete.
    */
    const seBusca =
      opciones.length >= DESDE_CUANTAS_SE_BUSCA || Boolean(onCrear) || Boolean(onBuscar);
    const texto = busqueda.trim();

    // Lo escrito, a quien busca en el servidor. Con la última versión de la
    // función, sin volver a llamarla solo porque el padre se volvió a pintar.
    const alBuscar = useRef(onBuscar);
    alBuscar.current = onBuscar;
    useEffect(() => {
      alBuscar.current?.(texto);
    }, [texto]);
    // El primer pintado de la lista es invisible, mientras se mide si cabe.
    const colocada = sitio !== null;

    const filas = useMemo<OpcionDesplegable[]>(() => {
      const halladas =
        texto && !onBuscar
          ? opciones.filter((o) => contieneSinTildes(o.etiqueta, texto))
          : opciones;
      return opcionNinguna !== undefined && !texto
        ? [{ valor: "", etiqueta: opcionNinguna }, ...halladas]
        : halladas;
    }, [opciones, opcionNinguna, texto, onBuscar]);

    // Lo que dice el botón cerrado.
    const coincide =
      opciones.find((o) => o.valor === valor) ??
      (opcionNinguna !== undefined && valor === ""
        ? { valor: "", etiqueta: opcionNinguna }
        : undefined);
    const mostrada =
      coincide ?? (siNoCoincide === "primera" ? opciones.find((o) => !o.desactivada) : undefined);
    const valorMostrado = mostrada?.valor;
    // La de «ninguna», «Todas…» o «Elige…» va en gris: no es un dato elegido.
    const enGris = !mostrada || mostrada.valor === "";

    const puedeCrear =
      Boolean(onCrear) &&
      texto.length > 1 &&
      !opciones.some((o) => o.etiqueta.toLowerCase() === texto.toLowerCase());

    function unirRef(nodo: HTMLButtonElement | null) {
      boton.current = nodo;
      if (typeof refExterno === "function") refExterno(nodo);
      else if (refExterno) refExterno.current = nodo;
    }

    // ── Abrir y cerrar ───────────────────────────────────────────────────────

    /*
      ⚠️ LA MARCADA SE DECIDE AL ABRIR, EN EL MISMO GESTO.

      Antes la decidía un efecto, un instante después de abrirse. Un cajero
      rápido que abre y baja con la flecha de golpe llegaba antes que el
      efecto, y el efecto le pisaba la flecha: la marca volvía a la elegida.
    */
    function abrir(conTexto = "", marcar?: number) {
      if (disabled) return;
      setHoja(forma !== "campo" && (window.matchMedia?.("(max-width: 639px)")?.matches ?? false));
      const elegida = filas.findIndex((f) => f.valor === valorMostrado && !f.desactivada);
      setBusqueda(conTexto);
      // Abriendo con texto se busca: la marcada es la primera que aparezca.
      setActiva(marcar ?? (conTexto ? 0 : elegida >= 0 ? elegida : primeraDesde(0, 1)));
      setAbierto(true);
    }

    function cerrar(devolverFoco: boolean) {
      setAbierto(false);
      setBusqueda("");
      setActiva(-1);
      if (devolverFoco) boton.current?.focus({ preventScroll: true });
    }

    function elegir(fila: OpcionDesplegable | undefined) {
      if (!fila || fila.desactivada) return;
      // Como el `<select>`: elegir lo que ya estaba no es un cambio.
      if (fila.valor !== valor) onElegir(fila.valor);
      cerrar(true);
    }

    // La marcada siempre a la vista, también al moverse con las flechas. Con
    // la lista ya colocada: antes, el desplazamiento se hacía sobre una lista
    // que todavía no tenía su alto.
    useEffect(() => {
      if (!abierto || !colocada || activa < 0) return;
      document.getElementById(idFila(activa))?.scrollIntoView?.({ block: "nearest" });
    }, [abierto, colocada, activa]);

    /*
      El buscador se enfoca solo con ratón, no en el celular.

      En un teléfono, enfocarlo abre el teclado, que tapa media pantalla y la
      mitad de la lista que se acaba de abrir. Quien quiere buscar toca el
      cuadro; quien quiere elegir, elige.
    */
    //
    // ⚠️ Cuando la lista ya está COLOCADA, no al abrirse: el primer pintado es
    // invisible (se está midiendo), y a algo invisible el navegador no le da
    // el foco — sin avisar. Se vio: la lista se abría y lo que se tecleaba no
    // llegaba al buscador.
    useEffect(() => {
      if (!abierto || !colocada || !seBusca) return;
      const conRaton = window.matchMedia?.("(pointer: fine)")?.matches ?? true;
      if (conRaton || busqueda) buscador.current?.focus({ preventScroll: true });
    }, [abierto, colocada]);

    // ── Dónde se pinta ───────────────────────────────────────────────────────

    /*
      ⚠️ LA LISTA SE DIBUJA FUERA, PEGADA AL `body`.

      Dentro de un modal, un `absolute` lo recorta el `overflow` del modal: la
      lista quedaba encerrada en la ventanita y lo de abajo no se alcanzaba.
      Con un portal sale del recorte y se coloca a mano, en coordenadas de
      pantalla.
    */
    useLayoutEffect(() => {
      if (!abierto) {
        entradaDesde.current = null;
        return setSitio(null);
      }

      const colocar = (e?: Event) => {
        const b = boton.current;
        if (!b) return;
        const r = b.getBoundingClientRect();
        const altoVentana = window.innerHeight;
        const anchoVentana = window.innerWidth;

        /*
          ⚠️ LO QUE SE VE, NO LO QUE MIDE LA VENTANA.

          En el celular, el teclado tapa la mitad de abajo sin que la ventana
          cambie de tamaño: solo se achica la parte visible (`visualViewport`).
          Midiendo la ventana, la lista se abría debajo del teclado.
        */
        const vista = window.visualViewport;
        const bordeArriba = vista ? vista.offsetTop : 0;
        const bordeAbajo = vista ? vista.offsetTop + vista.height : altoVentana;
        const escribiendo = document.activeElement === buscador.current;

        // La hoja de abajo va pegada al borde de lo que se ve (encima del
        // teclado, si está abierto) y ocupa hasta un 70 % del alto.
        if (hoja) {
          entradaDesde.current ??= "abajo";
          return setSitio({
            bottom: altoVentana - bordeAbajo,
            left: 0,
            minAncho: 0,
            maxAlto: Math.max(200, (bordeAbajo - bordeArriba) * 0.7),
            haciaArriba: false,
          });
        }

        // Si el campo se fue de la pantalla al DESPLAZAR el fondo, la lista se
        // cierra: flotando sola, parecería de otro sitio. Al cambiar de tamaño
        // no, y nunca mientras se escribe en el buscador: abrir el teclado del
        // celular mueve todo, y la lista se cerraba en la cara de quien buscaba.
        const fuera = r.bottom < bordeArriba || r.top > bordeAbajo;
        if (fuera && e?.type === "scroll" && !escribiendo) return cerrar(false);

        const debajo = bordeAbajo - r.bottom - SEPARACION - MARGEN;
        const encima = r.top - bordeArriba - SEPARACION - MARGEN;
        const quiere = Math.min(panel.current?.scrollHeight ?? ALTO_MAXIMO, ALTO_MAXIMO);
        // ⚠️ Hacia arriba solo si abajo no cabe Y arriba hay más sitio. En un
        // celular, un campo de la mitad de abajo abría una lista que se salía
        // por el borde y no se podía desplazar.
        const haciaArriba = debajo < quiere && encima > debajo;
        entradaDesde.current ??= haciaArriba ? "arriba" : "abajo";

        // El panel puede ser más ancho que el campo —para que «USD — Dólar
        // estadounidense» se lea entero en un campo estrecho—, pero nunca
        // salirse por la derecha.
        const ancho = Math.max(panel.current?.offsetWidth ?? 0, r.width);
        const left = Math.max(MARGEN, Math.min(r.left, anchoVentana - MARGEN - ancho));

        setSitio({
          top: haciaArriba ? undefined : r.bottom + SEPARACION,
          bottom: haciaArriba ? altoVentana - r.top + SEPARACION : undefined,
          left,
          minAncho: r.width,
          maxAlto: Math.max(120, Math.min(haciaArriba ? encima : debajo, ALTO_MAXIMO)),
          haciaArriba,
        });
      };

      colocar();
      window.addEventListener("scroll", colocar, true);
      window.addEventListener("resize", colocar);
      window.visualViewport?.addEventListener("resize", colocar);
      return () => {
        window.removeEventListener("scroll", colocar, true);
        window.removeEventListener("resize", colocar);
        window.visualViewport?.removeEventListener("resize", colocar);
      };
    }, [abierto, filas.length, hoja]);

    // ── Tocar fuera ──────────────────────────────────────────────────────────

    useEffect(() => {
      if (!abierto) return;

      const alTocar = (e: PointerEvent) => {
        const donde = e.target as Node;
        if (boton.current?.contains(donde) || panel.current?.contains(donde)) return;
        cerrar(false);

        /*
          ⚠️ EL PRIMER TOQUE FUERA SOLO CIERRA LA LISTA.

          Es lo que hace el `<select>` del navegador, y aquí importa de verdad:
          la lista de «Moneda del vuelto» está dentro de la ventana del cobro.
          Sin esto, tocar fuera para cerrarla podía caer en el fondo oscuro y
          cerrar el cobro entero, o pulsar el botón que hubiera debajo.
        */
        //
        // ⚠️ Se traga UN clic y se quita sola. Antes se quitaba con un
        // temporizador, y el navegador los retrasa (en segundo plano, en un
        // celular justo de recursos): el segundo toque en el fondo tampoco
        // llegaba y la ventana del cobro no se podía cerrar.
        const tragar = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
          soltar();
        };
        // Si ese toque no acaba en clic (se desplazó la pantalla con el dedo,
        // o fue el botón derecho), el siguiente toque ya es normal.
        const soltar = () => {
          document.removeEventListener("click", tragar, true);
          document.removeEventListener("pointercancel", soltar, true);
          document.removeEventListener("pointerdown", soltar, true);
        };
        document.addEventListener("click", tragar, true);
        document.addEventListener("pointercancel", soltar, true);
        // Añadido durante este mismo `pointerdown`, no lo escucha: empieza a
        // contar desde el siguiente toque.
        document.addEventListener("pointerdown", soltar, true);
      };

      document.addEventListener("pointerdown", alTocar, true);
      return () => document.removeEventListener("pointerdown", alTocar, true);
    }, [abierto]);

    // ── Teclado ──────────────────────────────────────────────────────────────

    /** La primera fila elegible desde `i`, avanzando en `paso` (±1). */
    function primeraDesde(i: number, paso: 1 | -1): number {
      for (let j = i; j >= 0 && j < filas.length; j += paso) {
        if (!filas[j]?.desactivada) return j;
      }
      return -1;
    }

    function mover(paso: 1 | -1, cuantas = 1) {
      let destino = activa;
      for (let k = 0; k < cuantas; k++) {
        const siguiente = primeraDesde(destino + paso, paso);
        if (siguiente < 0) break;
        destino = siguiente;
      }
      if (destino >= 0) setActiva(destino);
    }

    /**
     * Saltar escribiendo, como en el `<select>`: «v» lleva a VES, y «ve» sigue
     * en VES en vez de saltar a la siguiente que empiece por «e».
     */
    function indicePorLetra(letra: string): number {
      const ahora = Date.now();
      const l = letras.current;
      l.texto = ahora < l.hasta ? l.texto + letra : letra;
      l.hasta = ahora + 700;
      const buscado = sinTildes(l.texto);

      // Repetir la misma letra recorre las que empiezan por ella.
      const repetida = buscado.length > 1 && [...buscado].every((c) => c === buscado[0]);
      const prefijo = repetida ? buscado.charAt(0) : buscado;
      const desde = repetida || buscado.length === 1 ? activa + 1 : activa;

      for (let k = 0; k < filas.length; k++) {
        const j = (Math.max(desde, 0) + k) % filas.length;
        const fila = filas[j];
        if (fila && !fila.desactivada && sinTildes(fila.etiqueta).startsWith(prefijo)) return j;
      }
      return -1;
    }

    function saltarConLetra(letra: string) {
      const j = indicePorLetra(letra);
      if (j >= 0) setActiva(j);
    }

    const esLetra = (e: KeyboardEvent) =>
      e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

    function alTeclear(e: KeyboardEvent<HTMLElement>) {
      const enBuscador = e.currentTarget === buscador.current;

      if (!abierto) {
        if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
          e.preventDefault();
          abrir();
        } else if (esLetra(e)) {
          e.preventDefault();
          if (seBusca) abrir(e.key);
          else {
            const j = indicePorLetra(e.key);
            abrir("", j >= 0 ? j : undefined);
          }
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          return mover(1);
        case "ArrowUp":
          e.preventDefault();
          return mover(-1);
        case "PageDown":
          e.preventDefault();
          return mover(1, 8);
        case "PageUp":
          e.preventDefault();
          return mover(-1, 8);
        case "Home":
          if (enBuscador) return;
          e.preventDefault();
          return setActiva(primeraDesde(0, 1));
        case "End":
          if (enBuscador) return;
          e.preventDefault();
          return setActiva(primeraDesde(filas.length - 1, -1));
        case "Enter":
          e.preventDefault();
          if (activa < 0 && puedeCrear) return void crear();
          return elegir(filas[activa]);
        case " ":
          // En el buscador el espacio se escribe.
          if (enBuscador) return;
          e.preventDefault();
          return elegir(filas[activa]);
        case "Escape":
          /*
            ⚠️ `stopPropagation`: el modal también escucha el Escape para
            cerrarse. Con la lista abierta, Escape cierra la lista y nada más;
            el siguiente, ya sí, cierra la ventana.
          */
          e.preventDefault();
          e.stopPropagation();
          return cerrar(true);
        case "Tab":
          // Desde el buscador, el foco se iría al final de la página (la lista
          // está pegada al `body`): se devuelve al campo.
          if (enBuscador) e.preventDefault();
          return cerrar(enBuscador);
        default:
          if (!enBuscador && esLetra(e)) {
            e.preventDefault();
            saltarConLetra(e.key);
          }
      }
    }

    async function crear() {
      if (!onCrear || !puedeCrear) return;
      await onCrear(texto);
      cerrar(true);
    }

    // ── Pintar ───────────────────────────────────────────────────────────────

    const separarLaPrimera = !texto && filas.length > 1 && filas[0]?.valor === "";

    // La pastilla está «puesta» cuando filtra algo. Si lo elegido ya no está
    // entre las opciones (una marca que se borró), se sigue enseñando, para
    // que se vea qué filtra y se pueda quitar.
    const puesta = forma === "pastilla" && valor !== "";
    const textoPuesto = coincide?.etiqueta ?? valor;
    const rotuloOrden = mostrada?.etiqueta ?? textoSinElegir;

    return (
      <>
        {forma === "pastilla" ? (
          <span
            className={unir(
              "inline-flex h-[34px] max-w-full shrink-0 items-center rounded-full border text-[13.5px] pointer-coarse:h-10",
              "transition-[border-color,background-color,color] duration-150",
              puesta
                ? "border-accent/40 bg-accent-soft text-accent"
                : abierto
                  ? "border-accent bg-surface text-ink"
                  : "border-line-strong bg-surface text-ink-soft hover:bg-surface-hover hover:text-ink",
              disabled && "opacity-60",
              className,
            )}
          >
            <button
              ref={unirRef}
              id={idCampo}
              type="button"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={abierto}
              aria-controls={abierto ? idLista : undefined}
              aria-activedescendant={abierto && activa >= 0 && !seBusca ? idFila(activa) : undefined}
              aria-label={ariaLabel ?? (puesta ? `${nombre}: ${textoPuesto}` : nombre)}
              title={title}
              disabled={disabled}
              onClick={() => (abierto ? cerrar(false) : abrir())}
              onKeyDown={alTeclear}
              onKeyUp={(e) => e.key === " " && e.preventDefault()}
              className={unir(
                "flex h-full min-w-0 items-center gap-1.5 rounded-full pl-3 focus:outline-none",
                "focus-visible:ring-2 focus-visible:ring-accent/40",
                puesta ? "pr-1" : "pr-2.5",
              )}
            >
              {puesta ? (
                <>
                  <span className="shrink-0 opacity-80">{nombre}:</span>
                  <span className="min-w-0 max-w-[13rem] truncate font-semibold">{textoPuesto}</span>
                </>
              ) : (
                <>
                  <span className="font-medium">{nombre}</span>
                  <ChevronDown
                    size={15}
                    aria-hidden
                    className={unir(
                      "shrink-0 transition-transform duration-200",
                      abierto ? "rotate-180 text-accent" : "text-faint",
                    )}
                  />
                </>
              )}
            </button>
            {puesta && (
              <button
                type="button"
                onClick={() => onElegir("")}
                disabled={disabled}
                aria-label={`Quitar el filtro de ${(nombre ?? "").toLowerCase()}`}
                title="Quitar este filtro"
                className={unir(
                  "mr-1 grid size-6 shrink-0 place-items-center rounded-full transition-colors",
                  "hover:bg-accent/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  "pointer-coarse:size-8",
                )}
              >
                <X size={14} strokeWidth={2.5} aria-hidden />
              </button>
            )}
          </span>
        ) : forma === "orden" ? (
          <button
            ref={unirRef}
            id={idCampo}
            type="button"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={abierto}
            aria-controls={abierto ? idLista : undefined}
            aria-activedescendant={abierto && activa >= 0 && !seBusca ? idFila(activa) : undefined}
            aria-label={ariaLabel ?? `${nombre ?? "Ordenar"}: ${rotuloOrden}`}
            title={title ?? `${nombre ?? "Ordenar"}: ${rotuloOrden}`}
            disabled={disabled}
            onClick={() => (abierto ? cerrar(false) : abrir())}
            onKeyDown={alTeclear}
            onKeyUp={(e) => e.key === " " && e.preventDefault()}
            className={unir(
              "group/desplegable flex h-11 shrink-0 items-center justify-center gap-2 rounded-[var(--radius-control)] border bg-surface text-sm",
              "w-11 sm:w-auto sm:justify-start sm:pl-3 sm:pr-2.5",
              "transition-[border-color,box-shadow,background-color] duration-150",
              "focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30",
              "disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-faint",
              abierto ? "border-accent ring-2 ring-accent/30" : "border-line-strong enabled:hover:border-accent/60",
              className,
            )}
          >
            <ArrowUpDown size={16} aria-hidden className="shrink-0 text-muted" />
            <span className="hidden whitespace-nowrap sm:inline">
              <span className="text-muted">{nombre ?? "Ordenar"}:</span>{" "}
              <span className="text-ink">{rotuloOrden}</span>
            </span>
            <ChevronDown
              size={16}
              aria-hidden
              className={unir(
                "hidden shrink-0 transition-transform duration-200 sm:block",
                abierto ? "rotate-180 text-accent" : "text-muted group-hover/desplegable:text-ink-soft",
              )}
            />
          </button>
        ) : (
        <button
          ref={unirRef}
          id={idCampo}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={abierto}
          aria-controls={abierto ? idLista : undefined}
          aria-activedescendant={abierto && activa >= 0 && !seBusca ? idFila(activa) : undefined}
          aria-label={ariaLabel}
          aria-invalid={error || undefined}
          title={title}
          autoFocus={autoFocus}
          disabled={disabled}
          onClick={() => (abierto ? cerrar(false) : abrir())}
          onKeyDown={alTeclear}
          // El espacio activa un botón al SOLTARLO: sin esto, elegir con
          // espacio volvía a abrir la lista en el mismo gesto.
          onKeyUp={(e) => e.key === " " && e.preventDefault()}
          className={unir(
            "group/desplegable flex items-center gap-2 text-left",
            "rounded-[var(--radius-control)] border bg-surface text-ink",
            "transition-[border-color,box-shadow,background-color] duration-150",
            !pide(className, /(^|\s)w-/) && "w-full",
            !pide(className, /(^|\s)h-/) && "h-11",
            !pide(className, /(^|\s)(pl|px)-/) && "pl-3.5",
            !pide(className, /(^|\s)(pr|px)-/) && "pr-2.5",
            "focus:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30",
            "disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-faint",
            error
              ? "border-danger"
              : abierto
                ? "border-accent ring-2 ring-accent/30"
                : "border-line-strong enabled:hover:border-accent/60",
            className,
          )}
        >
          {mostrada?.color && (
            <span
              className="size-2.5 shrink-0 rounded-full ring-1 ring-black/5"
              style={{ backgroundColor: mostrada.color }}
              aria-hidden
            />
          )}
          <span className={unir("min-w-0 flex-1 truncate", enGris && "text-muted")}>
            {mostrada?.etiqueta ?? textoSinElegir}
          </span>
          <ChevronDown
            size={16}
            aria-hidden
            className={unir(
              "shrink-0 transition-transform duration-200",
              abierto ? "rotate-180 text-accent" : "text-muted group-hover/desplegable:text-ink-soft",
            )}
          />
        </button>
        )}

        {/* En la hoja de abajo, el fondo se oscurece: lo de detrás no se toca. */}
        {abierto &&
          hoja &&
          createPortal(<div aria-hidden className="animar-velo fixed inset-0 z-[59] bg-black/40" />, document.body)}

        {abierto &&
          createPortal(
            <div
              ref={panel}
              // Lo que se toca aquí no es de la pantalla de debajo: sin esto, un
              // clic en una opción subía (por el árbol de React, portal incluido)
              // hasta la tarjeta o la fila que contiene el campo.
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: sitio?.top,
                bottom: sitio?.bottom,
                left: sitio?.left ?? -9999,
                right: hoja ? 0 : undefined,
                minWidth: sitio?.minAncho,
                maxWidth: hoja ? "none" : `min(24rem, calc(100vw - ${MARGEN * 2}px))`,
                maxHeight: sitio?.maxAlto ?? ALTO_MAXIMO,
                // Se pinta invisible la primera vez, mientras se mide si cabe
                // abajo. Sin esto pega un salto al abrirse.
                visibility: sitio ? "visible" : "hidden",
              }}
              className={unir(
                hoja
                  ? "animar-hoja rounded-t-[20px] border-t border-line px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1"
                  : entradaDesde.current === "arriba"
                    ? "animar-desplegable-arriba"
                    : "animar-desplegable",
                "z-[60] flex flex-col overflow-hidden bg-surface",
                !hoja && "rounded-[var(--radius-card)] border border-line p-1.5",
                "shadow-[var(--shadow-pop)]",
              )}
            >
              {hoja && (
                <div className="shrink-0">
                  <div aria-hidden className="mx-auto mb-2 mt-1 h-1 w-10 rounded-full bg-line-strong" />
                  <p className="mb-2 px-1 text-base font-semibold text-ink">{nombre ?? textoSinElegir}</p>
                </div>
              )}
              {seBusca && (
                <div className="relative mb-1.5 shrink-0">
                  <Search
                    size={15}
                    aria-hidden
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint"
                  />
                  <input
                    ref={buscador}
                    value={busqueda}
                    onChange={(e) => {
                      setBusqueda(e.target.value);
                      // Al buscar, la marcada es la primera que aparece.
                      setActiva(0);
                    }}
                    onKeyDown={alTeclear}
                    aria-controls={idLista}
                    aria-activedescendant={activa >= 0 ? idFila(activa) : undefined}
                    aria-label="Buscar en la lista"
                    autoComplete="off"
                    /*
                      ⚠️ El texto DICE que se puede crear. Antes ponía «Buscar…»
                      y la opción de crear solo aparecía al escribir algo que no
                      existía: para descubrirlo había que intentarlo sin saber
                      que se podía. El dueño: «no está implícito y la gente no
                      sabría».
                    */
                    placeholder={onCrear ? "Buscar o escribir uno nuevo…" : "Buscar…"}
                    className={unir(
                      "h-9 w-full rounded-[calc(var(--radius-control)-2px)] border border-line bg-surface-2 pl-8 pr-3",
                      "text-sm text-ink placeholder:text-faint transition-colors",
                      "focus:border-accent focus:bg-surface focus:outline-none focus:ring-2 focus:ring-accent/25",
                    )}
                  />
                </div>
              )}

              <div
                id={idLista}
                role="listbox"
                // Se llama como el campo, sea por su etiqueta o por su `aria-label`.
                aria-labelledby={idCampo}
                className="scroll-fino min-h-0 flex-1 overflow-y-auto overscroll-contain"
              >
                {filas.map((f, i) => {
                  const elegida = f.valor === valorMostrado;
                  const marcada = i === activa;
                  return (
                    <div key={`${i}-${f.valor}`}>
                      <div
                        id={idFila(i)}
                        role="option"
                        aria-selected={elegida}
                        aria-disabled={f.desactivada || undefined}
                        // El foco se queda en el campo (o en el buscador): así el
                        // teclado sigue funcionando después de tocar con el ratón.
                        onMouseDown={(e) => e.preventDefault()}
                        // `mousemove` y no `mouseenter`: al bajar con las flechas la
                        // lista se desplaza bajo el ratón quieto, y con `mouseenter`
                        // la marca saltaba a donde estuviera el puntero.
                        onMouseMove={() => !marcada && !f.desactivada && setActiva(i)}
                        onClick={() => elegir(f)}
                        className={unir(
                          "flex min-h-10 select-none items-center gap-2.5 px-2.5 py-2 text-sm",
                          "pointer-coarse:min-h-12",
                          "rounded-[calc(var(--radius-control)-2px)] transition-colors duration-100",
                          f.desactivada
                            ? "cursor-not-allowed text-faint"
                            : elegida
                              ? "cursor-pointer bg-accent-soft font-medium text-accent"
                              : marcada
                                ? "cursor-pointer bg-surface-hover text-ink"
                                : "cursor-pointer text-ink",
                          f.valor === "" && !elegida && !f.desactivada && "text-muted",
                        )}
                      >
                        {f.color && (
                          <span
                            className="size-2.5 shrink-0 rounded-full ring-1 ring-black/5"
                            style={{ backgroundColor: f.color }}
                            aria-hidden
                          />
                        )}
                        <span className="min-w-0 flex-1 break-words">
                          <span className="block">{f.etiqueta}</span>
                          {f.pie && (
                            <span className="block text-xs font-normal text-muted">{f.pie}</span>
                          )}
                        </span>
                        <Check
                          size={16}
                          strokeWidth={2.5}
                          aria-hidden
                          className={unir("shrink-0 text-accent", !elegida && "invisible")}
                        />
                      </div>
                      {/* «Todas…», «Ninguna» y «Elige…» no son una opción más: una
                          raya las separa de las de verdad. */}
                      {i === 0 && separarLaPrimera && (
                        <div role="separator" className="mx-2 my-1 h-px bg-line" />
                      )}
                    </div>
                  );
                })}

                {filas.length === 0 && !puedeCrear && quedan === 0 && (
                  <p className="px-3 py-5 text-center text-sm text-muted">
                    {texto ? <>Nada con «{texto}».</> : "No hay opciones."}
                  </p>
                )}

                {onVerMas && (
                  <VerMas quedan={quedan} onVerMas={onVerMas} cargando={cargandoMas} />
                )}
              </div>

              {/*
                La invitación a crear, ANTES de escribir nada. Sin ella, quien
                abre la lista y no encuentra lo suyo la cierra pensando que hay
                que ir a otra pantalla.
              */}
              {onCrear && !texto && (
                <p className="mt-1.5 shrink-0 border-t border-line px-3 pt-2 text-xs text-muted">
                  {pistaCrear}
                </p>
              )}

              {puedeCrear && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void crear()}
                  className={unir(
                    "mt-1.5 flex w-full shrink-0 items-center gap-2 border-t border-line px-3 py-2.5",
                    "rounded-[calc(var(--radius-control)-2px)] text-left text-sm font-medium text-accent",
                    "transition-colors hover:bg-accent-soft",
                  )}
                >
                  <Plus size={15} className="shrink-0" aria-hidden />
                  {verboCrear} «{texto}»
                </button>
              )}
            </div>,
            document.body,
          )}
      </>
    );
  },
);

